/**
 * Order Edit Step View Component
 * Handles the deeper level navigation for individual step editing
 */

import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'
import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {useIsFieldDirty} from '@hconnect/tuc-processes/form/isDirty/useIsFieldDirty'
import {useOrderProcessFormContext} from '@hconnect/tuc-processes/form/useOrderProcessFormContext'
import {ArrowBack} from '@mui/icons-material'
import {Box, Typography, Button} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {OrderIntakeQuantityStep} from '../../orderIntake/components/fields/Quantity'
import {OrderIntakeTimeStep} from '../../orderIntake/components/fields/Time/OrderIntakeTimeStep'
import {RmcOrderUpdateOptions} from '../../orderIntake/types/order'
import {OrderIntakeSite} from '../../orderIntake/types/OrderIntakeSite.types'
import {formatJobSiteInfo} from '../../orderIntake/utils/jobSiteUtils'

import {ResetMaterialVariationsButton} from './components/buttons/ResetMaterialVariationsButton'
import {MaterialVariationsStep} from './components/fields'
import {DateStep} from './components/fields/Date'
import {DriverInstructionsField} from './components/fields/DriverInstructions/DriverInstructionsField'
import {IntervalsStep} from './components/fields/Intervals'
import {MaterialStep} from './components/fields/Material'
import {NotesField} from './components/fields/Notes/NotesField'
import {PoNumberField} from './components/fields/PoNumber/PoNumberField'
import {RetarderField} from './components/fields/Retarder/RetarderField'
import {UnloadingMethodStep} from './components/fields/UnloadingMethod'
import {UsageStep} from './components/fields/Usage'
import {useMaterialVariationsState} from './hooks/useMaterialVariationsState'

/**
 * Available step types for edit mode
 */
export enum EditableFieldType {
  QUOTE = 'quote',
  MATERIAL = 'material',
  MIX_CHARACTERISTICS = 'mix_characteristics',
  USAGE = 'usage',
  QUANTITY = 'quantity',
  UNLOADING_METHOD = 'unloading_method',
  DATE = 'date',
  INTERVALS = 'intervals',
  TIME = 'time',
  PO_NUMBER = 'po_number',
  DRIVER_INSTRUCTIONS = 'driver_instructions',
  ADDITIONAL_NOTES = 'additional_notes',
  RETARDER = 'retarder'
}
export interface StepCompleteResult {
  stepType: EditableFieldType
  hasChanges: boolean
}

/**
 * Props for the Order Edit Step View component
 */
interface RmcOrderEditStepViewProps {
  stepType: EditableFieldType
  onBack: () => void
  onStepComplete?: (result: StepCompleteResult) => void
  onOrderUpdate?: (formData: FieldsForm, options?: RmcOrderUpdateOptions) => void
  jobSite: OrderIntakeSite | null
}

/**
 * Component for editing individual steps in order management
 */
export const RmcOrderEditStepView: React.FC<RmcOrderEditStepViewProps> = ({
  stepType,
  onBack,
  onStepComplete,
  onOrderUpdate,
  jobSite
}) => {
  const {t} = useTranslation()
  const {getValues, reset} = useOrderProcessFormContext()

  const initialFormSnapshotRef = React.useRef<FieldsForm | null>(null)
  React.useEffect(() => {
    initialFormSnapshotRef.current = structuredClone(getValues())
  }, [getValues, stepType])

  const isDateTimeDirty = useIsFieldDirty({field: 'dateTime'})

  // Get material variations state for reset functionality
  const {resetMaterialVariations} = useMix()
  const {shouldShowResetButton} = useMaterialVariationsState(stepType)

  /**
   * Step configuration for different step types
   */
  const getStepConfig = (stepType: EditableFieldType) => {
    switch (stepType) {
      case EditableFieldType.MATERIAL:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.material'),
          component: <MaterialStep />
        }
      case EditableFieldType.MIX_CHARACTERISTICS:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.materialVariations'),
          component: <MaterialVariationsStep />
        }
      case EditableFieldType.USAGE:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.usage'),
          component: <UsageStep />
        }
      case EditableFieldType.QUANTITY:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.quantity'),
          component: <OrderIntakeQuantityStep />
        }
      case EditableFieldType.UNLOADING_METHOD:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.unloadingMethod'),
          component: <UnloadingMethodStep />
        }
      case EditableFieldType.INTERVALS:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.intervals'),
          component: <IntervalsStep />
        }
      case EditableFieldType.DATE:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.date'),
          component: <DateStep />
        }
      case EditableFieldType.TIME:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.time'),
          component: <OrderIntakeTimeStep />
        }
      case EditableFieldType.PO_NUMBER:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.poNumber'),
          component: <PoNumberField />
        }
      case EditableFieldType.DRIVER_INSTRUCTIONS:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.driverInstructions'),
          component: <DriverInstructionsField />
        }
      case EditableFieldType.ADDITIONAL_NOTES:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.additionalNotes'),
          component: <NotesField />
        }
      case EditableFieldType.RETARDER:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.retarder'),
          component: <RetarderField />
        }
      // Future cases can be added here
      default:
        return {
          title: t('orderIntake.rmc.orderManagement.editSteps.default'),
          component: <Box>{t('orderIntake.rmc.orderManagement.editSteps.notImplemented')}</Box>
        }
    }
  }

  const stepConfig = getStepConfig(stepType)

  // Handler for when the user confirms changes in the step
  const handleSelectAndContinue = () => {
    // Update the order with pending changes
    if (onOrderUpdate) {
      onOrderUpdate(getValues())
    }

    // Check if the dateTime field is dirty (only relevant for Date step)
    const hasChanges = stepType === EditableFieldType.DATE ? isDateTimeDirty : false

    // Call step complete callback if provided
    if (onStepComplete) {
      onStepComplete({stepType, hasChanges})
    } else {
      onBack()
    }
  }

  const handleDiscardChanges = () => {
    const snapshot = initialFormSnapshotRef.current

    if (snapshot) {
      reset(snapshot, {
        keepDefaultValues: true
      })
    }

    onBack()
  }

  // Determine if the step is valid and can proceed
  // const canProceed = stepType === EditStepType.TIME ? timeStepValidation.isValid : true
  const canProceed = true

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}
    >
      {/* Header with back navigation */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Button
            onClick={handleDiscardChanges}
            sx={{
              minWidth: 'auto',
              padding: '4px',
              color: '#016ad4',
              marginRight: '12px'
            }}
            data-test-id="step-edit-back-button"
          >
            <ArrowBack sx={{fontSize: '16px'}} />
          </Button>
          <Typography
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              lineHeight: '24px',
              color: '#29333d',
              fontFamily: 'Inter'
            }}
          >
            {t('orderIntake.rmc.orderManagement.editOrder')}
          </Typography>
        </Box>
      </Box>

      {/* Step Title */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            color: '#29333D',
            fontWeight: 600,
            fontSize: '20px',
            lineHeight: '24px',
            mb: 0.5
          }}
        >
          {stepConfig.title}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: '#52667A',
            fontSize: '12px',
            lineHeight: '16px'
          }}
        >
          {formatJobSiteInfo(jobSite)}
        </Typography>
      </Box>

      {/* Step Content */}
      <Box sx={{flexGrow: 1}} key={stepType}>
        {stepConfig.component}
      </Box>

      {/* Action Buttons */}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
        {/* Reset to default values button - only for material variations step with configured values */}
        {shouldShowResetButton && (
          <ResetMaterialVariationsButton
            onReset={() => resetMaterialVariations?.()}
            testId="reset-material-variations-order-edit-button"
            variant="fixed"
          />
        )}

        <Button
          variant="contained"
          fullWidth
          onClick={handleSelectAndContinue}
          disabled={!canProceed}
          sx={{
            height: '48px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#ffffff',
            backgroundColor: canProceed ? '#016ad4' : '#d1d9e0',
            fontFamily: 'Inter',
            '&:hover': {
              backgroundColor: canProceed ? '#014ba8' : '#d1d9e0'
            },
            '&:disabled': {
              backgroundColor: '#d1d9e0',
              color: '#888',
              cursor: 'not-allowed'
            }
          }}
          data-test-id="step-edit-select-continue-button"
        >
          {t('orderIntake.rmc.stepper.buttons.selectAndContinue')}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          onClick={handleDiscardChanges}
          sx={{
            height: '48px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#da0901',
            borderColor: '#e8ecf0',
            backgroundColor: '#ffffff',
            fontFamily: 'Inter',
            '&:hover': {
              borderColor: '#da0901',
              backgroundColor: '#f7f9fa'
            }
          }}
          data-test-id="step-edit-discard-button"
        >
          {t('orderIntake.rmc.stepper.buttons.discardChanges')}
        </Button>
      </Box>
    </Box>
  )
}
